import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import axios from "axios";
import ChapterHeader from "@/components/landing/ChapterHeader";
import { MAPS_LINK } from "@/lib/constants";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = () => (
  <div className="flex gap-1" aria-label="5 stelle su 5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className="h-4 w-4 fill-gold text-gold" />
    ))}
  </div>
);

export default function Recensioni() {
  const { t } = useLang();
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then((r) => setReviews(r.data))
      .catch(() => {});
  }, []);

  return (
    <section id="recensioni" data-testid="recensioni-section" className="mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36">
      <ChapterHeader number="06" title={t.recensioni.title} subtitle={t.recensioni.subtitle} />
      {reviews.length === 0 ? (
        <p className="font-mono-alt text-xs uppercase tracking-[0.25em] text-stone-400">{t.recensioni.loading}</p>
      ) : (
        <div className="grid gap-8 md:grid-cols-3">
          {reviews.map((r, i) => {
            const initials = r.author
              .split(" ")
              .map((w) => w[0])
              .join("")
              .slice(0, 2)
              .toUpperCase();
            return (
              <motion.figure
                key={r.id}
                data-testid={`review-card-${i}`}
                initial={{ opacity: 0, y: 36 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col rounded-3xl border border-bordeaux/15 bg-white p-8 shadow-[0_25px_60px_-40px_rgba(171,24,43,0.4)]"
              >
                <Stars />
                <blockquote className="mt-5 flex-1 font-display text-lg italic leading-relaxed text-stone-700">
                  &ldquo;{r.text}&rdquo;
                </blockquote>
                <figcaption className="mt-7 flex items-center gap-4 border-t border-bordeaux/10 pt-5">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-bordeaux font-display text-base font-semibold text-ivory">
                    {initials}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-ink">{r.author}</p>
                    <p className="font-mono-alt text-[10px] uppercase tracking-[0.2em] text-stone-400">
                      Google · {r.date}
                    </p>
                  </div>
                </figcaption>
              </motion.figure>
            );
          })}
        </div>
      )}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.3 }}
        className="mt-12 text-center"
      >
        <a
          data-testid="recensioni-google-link"
          href={MAPS_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full border border-bordeaux/30 px-8 py-4 font-mono-alt text-xs uppercase tracking-[0.22em] text-bordeaux transition-colors duration-300 hover:bg-bordeaux hover:text-ivory"
        >
          {t.recensioni.cta}
        </a>
      </motion.div>
    </section>
  );
}
